import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import type {
  CampaignScheduleData,
  CampaignScheduleRow,
  EventEvolutionData,
  EventTimelineData,
} from "@nexus/widgets";
import type { JsonValue } from "@nexus/widget-sdk";
import { fetchLiveEventMonitoring } from "@/lib/live/liveEventMonitoringData";
import {
  EVENT_TIMELINE_QUERY,
  normalizeEventTimeline,
  type EventTimelineResponse,
} from "@/lib/live/eventTimelineData";
import type { DataResolver } from "@/lib/dashboard/resolverTypes";
import { getFilterValue, getTenantIds } from "./shared";

// ── Event Evolution ───────────────────────────────────────────────────────────

const EVENT_EVOLUTION_FIELDS = `
  protected_event_id
  event_id
  event_label
  event_type
  event_start
  event_end
  blocking_start
  blocking_end
  detection_start
  response_start
  impact_start
  engine_ids
  range_from
  range_to
  interval_minutes
  detections {
    bucket
    value
    cumulative
  }
  responses {
    bucket
    value
    cumulative
  }
  impacts {
    bucket
    value
    cumulative
  }
  expected_isps
  breakdown {
    value
    anomaly_count
    expected_detection_count
    harvesting_start
    anomalies
    anomaly_details {
      anomaly_id
      raised_at
      anomaly_type
      anomaly_severity
      anomaly_status
      anomaly_description
    }
    detections {
      bucket
      count
    }
    confirmed_responses {
      bucket
      count
    }
    impacts_confirmed {
      bucket
      count
    }
    monitoring_engines {
      engine_id
      engine_label
    }
  }
`;

const EVENT_EVOLUTION_QUERY = gql`
  query EventEvolution(
    $protected_event_id: Int!
    $from_ts: String!
    $to_ts: String!
    $interval_minutes: Int
    $engine_ids: [Int!]
    $filter_dimension: String
  ) {
    event_evolution(
      protected_event_id: $protected_event_id
      from_ts: $from_ts
      to_ts: $to_ts
      interval_minutes: $interval_minutes
      engine_ids: $engine_ids
      filter_dimension: $filter_dimension
    ) {
      ${EVENT_EVOLUTION_FIELDS}
    }
  }
`;

type EventEvolutionResponse = {
  event_evolution: {
    protected_event_id: number;
    event_id: string | null;
    event_label: string;
    event_type: string;
    event_start: string | null;
    event_end: string | null;
    blocking_start: string | null;
    blocking_end: string | null;
    detection_start: string | null;
    response_start: string | null;
    impact_start: string | null;
    engine_ids: number[];
    range_from: string;
    range_to: string;
    interval_minutes: number;
    detections: Array<{ bucket: string; value: number; cumulative: number }>;
    responses: Array<{ bucket: string; value: number; cumulative: number }>;
    impacts: Array<{ bucket: string; value: number; cumulative: number }>;
    expected_isps: number;
    breakdown: Array<{
      value: string;
      anomaly_count: number;
      expected_detection_count: number;
      harvesting_start: string | null;
      anomalies: string[];
      anomaly_details: Array<{
        anomaly_id: number;
        raised_at: string;
        anomaly_type: string;
        anomaly_severity: string;
        anomaly_status: string;
        anomaly_description: string | null;
      }>;
      detections: Array<{ bucket: string; count: number }>;
      confirmed_responses: Array<{ bucket: string; count: number }>;
      impacts_confirmed: Array<{ bucket: string; count: number }>;
      monitoring_engines: Array<{ engine_id: number; engine_label: string }>;
    }>;
  };
};

function normalizeEventEvolutionRow(
  row: EventEvolutionResponse["event_evolution"],
): EventEvolutionData {
  return {
    protected_event_id: Number(row.protected_event_id),
    event_id: row.event_id,
    event_label: row.event_label,
    event_type: row.event_type,
    event_start: row.event_start,
    event_end: row.event_end,
    blocking_start: row.blocking_start,
    blocking_end: row.blocking_end,
    detection_start: row.detection_start,
    response_start: row.response_start,
    impact_start: row.impact_start,
    engine_ids: (row.engine_ids ?? []).map((engineId) => Number(engineId)),
    range_from: row.range_from,
    range_to: row.range_to,
    interval_minutes: Number(row.interval_minutes ?? 0),
    detections: (row.detections ?? []).map((point) => ({
      timestamp: point.bucket,
      value: Number(point.value),
      cumulative: Number(point.cumulative),
    })),
    responses: (row.responses ?? []).map((point) => ({
      timestamp: point.bucket,
      value: Number(point.value),
      cumulative: Number(point.cumulative),
    })),
    impacts: (row.impacts ?? []).map((point) => ({
      timestamp: point.bucket,
      value: Number(point.value),
      cumulative: Number(point.cumulative),
    })),
    expected_isps: Number(row.expected_isps ?? 1),
    breakdown: (row.breakdown ?? []).map((entry) => ({
      value: entry.value,
      anomaly_count: Number(entry.anomaly_count),
      expected_detection_count: Number(entry.expected_detection_count),
      harvesting_start: entry.harvesting_start,
      anomalies: entry.anomalies ?? [],
      anomaly_details: entry.anomaly_details ?? [],
      detections: (entry.detections ?? []).map((p) => ({
        timestamp: p.bucket,
        count: Number(p.count),
      })),
      confirmed_responses: (entry.confirmed_responses ?? []).map((p) => ({
        timestamp: p.bucket,
        count: Number(p.count),
      })),
      impacts_confirmed: (entry.impacts_confirmed ?? []).map((p) => ({
        timestamp: p.bucket,
        count: Number(p.count),
      })),
      monitoring_engines: (entry.monitoring_engines ?? []).map((engine) => ({
        engine_id: Number(engine.engine_id),
        engine_label: engine.engine_label,
      })),
    })),
  };
}

function normalizeEventEvolution(raw: unknown): EventEvolutionData {
  const data = raw as EventEvolutionResponse;
  return normalizeEventEvolutionRow(data.event_evolution);
}

// ── Campaign Schedule ─────────────────────────────────────────────────────────

const CAMPAIGN_SCHEDULE_QUERY = gql`
  query CampaignSchedule($tenant_ids: [Int!]) {
    campaign_schedule(tenant_ids: $tenant_ids) {
      campaign_id
      campaign_label
      tenant_id
      tenant_label
      campaign_start
      campaign_end
      created_at
      created_by
      protected_event_id
      event_id
      stream_id
      stream_label
      channel
      event_label
      collection
      collection_group
      event_type_label
      event_start
      event_end
      risk_level
      popularity
      engine_id
      engine_label
      engine_type
      is_nagra
      engine_group_label
      infringement_types
      monitored_plateforms
      harvesting_window_minutes
      target_attributes
      response_types
      response_start_diff
      response_end_diff
      expected_contact_names
      off_stream_ids
    }
  }
`;

type CampaignScheduleResponseRow = {
  campaign_id: string;
  campaign_label: string;
  tenant_id: string;
  tenant_label: string;
  campaign_start: string;
  campaign_end: string;
  created_at: string;
  created_by: string;
  protected_event_id: string;
  event_id: string;
  stream_id: string;
  stream_label: string;
  channel: string;
  event_label: string;
  collection: string | null;
  collection_group: string | null;
  event_type_label: string;
  event_start: string;
  event_end: string;
  risk_level: number | null;
  popularity: number | null;
  engine_id: string;
  engine_label: string;
  engine_type: string;
  is_nagra: boolean;
  engine_group_label: string;
  infringement_types: string[];
  monitored_plateforms: string[];
  harvesting_window_minutes: number[];
  target_attributes: string[];
  response_types: string[];
  response_start_diff: number | null;
  response_end_diff: number | null;
  expected_contact_names: string[];
  off_stream_ids: number[];
};

type CampaignScheduleResponse = {
  campaign_schedule: CampaignScheduleResponseRow[];
};

function normalizeCampaignSchedule(raw: unknown): CampaignScheduleData {
  const data = raw as CampaignScheduleResponse;
  return {
    rows: (data.campaign_schedule ?? [])
      .filter((row) => {
        const engineType = row.engine_type?.toLowerCase();
        return engineType === "detection" || engineType === "response";
      })
      .map((row): CampaignScheduleRow => ({
        campaign_id: String(row.campaign_id),
        campaign_label: row.campaign_label,
        tenant_id: String(row.tenant_id),
        tenant_label: row.tenant_label,
        campaign_start: row.campaign_start,
        campaign_end: row.campaign_end,
        created_at: row.created_at,
        created_by: row.created_by,
        protected_event_id: String(row.protected_event_id),
        event_id: String(row.event_id),
        stream_id: String(row.stream_id),
        stream_label: row.stream_label,
        channel: row.channel,
        event_label: row.event_label,
        collection: row.collection,
        collection_group: row.collection_group,
        event_type_label: row.event_type_label,
        event_start: row.event_start,
        event_end: row.event_end,
        risk_level: row.risk_level != null ? Number(row.risk_level) : null,
        popularity: row.popularity != null ? Number(row.popularity) : null,
        engine_id: String(row.engine_id),
        engine_label: row.engine_label,
        engine_type: row.engine_type?.toLowerCase() === "response" ? "response" : "detection",
        is_nagra: Boolean(row.is_nagra),
        engine_group_label: row.engine_group_label,
        infringement_types: row.infringement_types ?? [],
        monitored_plateforms: row.monitored_plateforms ?? [],
        harvesting_window_minutes: (row.harvesting_window_minutes ?? []).map((value) =>
          Number(value),
        ),
        target_attributes: row.target_attributes ?? [],
        response_types: row.response_types ?? [],
        response_start_diff:
          row.response_start_diff != null ? Number(row.response_start_diff) : null,
        response_end_diff: row.response_end_diff != null ? Number(row.response_end_diff) : null,
        expected_contact_names: row.expected_contact_names ?? [],
        off_stream_ids: (row.off_stream_ids ?? []).map((value) => Number(value)),
      })),
  };
}

/**
 * Direct fetch for campaign schedule data — used by composition blocks and
 * hooks that bypass the dashboard widget system (e.g. useCampaignScheduleData).
 */
export async function fetchCampaignScheduleData(
  client: GraphQLClient,
  tenantIds?: number[],
  signal?: AbortSignal,
): Promise<CampaignScheduleData> {
  const variables = {
    tenant_ids: tenantIds && tenantIds.length > 0 ? tenantIds : undefined,
  };
  const raw = signal
    ? await client.request<CampaignScheduleResponse>({
        document: CAMPAIGN_SCHEDULE_QUERY,
        variables,
        signal,
      })
    : await client.request<CampaignScheduleResponse>(CAMPAIGN_SCHEDULE_QUERY, variables);
  return normalizeCampaignSchedule(raw);
}

// ── Resolvers ─────────────────────────────────────────────────────────────────

const EMPTY_EVENT_EVOLUTION: EventEvolutionData = normalizeEventEvolution({
  event_evolution: {
    protected_event_id: 0,
    event_id: null,
    event_label: "",
    event_type: "",
    event_start: null,
    event_end: null,
    blocking_start: null,
    blocking_end: null,
    detection_start: null,
    engine_ids: [],
    range_from: "",
    range_to: "",
    detections: [],
    responses: [],
    impacts: [],
    expected_isps: 1,
    breakdown: [],
  },
});

export const eventsDataSources: Record<string, DataResolver> = {
  "event-timeline": {
    contract: { name: "event-timeline", version: 1 },
    // deliberately no event_id filter — the timeline is the scope selector and
    // must always list every event in the tenant + time-range window.
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<EventTimelineResponse>(EVENT_TIMELINE_QUERY, params);
      return normalizeEventTimeline(raw);
    },
  },

  "event-timeline-advanced": {
    contract: { name: "event-timeline-advanced", version: 1 },
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
    }),
    fetcher: fetchLiveEventMonitoring,
  },

  "event-evolution": {
    contract: { name: "event-evolution", version: 1 },
    buildParams: (_requestParams, filters) => ({
      protected_event_id: (() => {
        if (typeof _requestParams.protectedEventId === "number") {
          return _requestParams.protectedEventId;
        }
        const protectedEventId = getFilterValue(filters, "protected_event_id");
        return protectedEventId ? Number(protectedEventId) : 0;
      })(),
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      interval_minutes: undefined,
      engine_ids: Array.isArray(_requestParams.engine_ids)
        ? (_requestParams.engine_ids as number[])
        : undefined,
      filter_dimension:
        typeof _requestParams.filterDimension === "string"
          ? (_requestParams.filterDimension as string)
          : undefined,
    }),
    fetcher: async (params, client: GraphQLClient) => {
      if (!params.protected_event_id) return EMPTY_EVENT_EVOLUTION;
      const raw = await client.request<EventEvolutionResponse>(EVENT_EVOLUTION_QUERY, params);
      return normalizeEventEvolution(raw);
    },
  },

  "event-scope": {
    contract: { name: "event-scope", version: 1 },
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      event_id: getFilterValue(filters, "event_id"),
      limit: 2000,
    }),
    fetcher: async (params, client: GraphQLClient) => {
      // No event scoped — skip the network round-trip and return empty data.
      if (!params.event_id) return { events: [] } as EventTimelineData;
      const raw = await client.request<EventTimelineResponse>(EVENT_TIMELINE_QUERY, params);
      return normalizeEventTimeline(raw);
    },
  },

  "campaign-schedule": {
    contract: { name: "campaign-schedule", version: 1 },
    // Hybrid scope: honours the tenant filter but ignores the time range — the
    // widget shows every campaign and splits them into active/past/future.
    buildParams: (_requestParams, filters) => ({
      tenant_ids: getTenantIds(filters),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      return fetchCampaignScheduleData(client, params.tenant_ids as number[] | undefined);
    },
  },
};

// Re-export for typing convenience
export type { JsonValue };
